export const WELCOME_OFFER_SYNC_EVENT = "eternua:welcome-offer-sync";
export const WELCOME_OFFER_SYNC_STORAGE_KEY = "eternua.welcome-offer.sync.v1";
export const WELCOME_OFFER_CHANNEL = "eternua-welcome-offer-v1";

export const WALLET_REFRESH_REQUESTED_EVENT = "eternua:wallet-refresh-requested";
// Reuse the rewards center's established invalidation event so every mounted
// summary consumer observes a confirmed welcome claim immediately.
export const REWARDS_REFRESH_REQUESTED_EVENT = "eternua:rewards-updated";
export const NOTIFICATIONS_REFRESH_REQUESTED_EVENT =
  "eternua:notifications-refresh-requested";

function smallHash(value) {
  let hash = 2166136261;
  const input = String(value || "anonymous");
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

export function welcomeOfferAccountScope(account) {
  return smallHash(account?.id ?? account?.username ?? account?.email ?? "anonymous");
}

function safeMessage(message) {
  if (!message || typeof message !== "object") return null;
  const type = String(message.type || "");
  if (!["claimed", "dismissed"].includes(type)) return null;
  return {
    type,
    accountScope: String(message.accountScope || ""),
    offerVersion: String(message.offerVersion || ""),
    at: Number(message.at) || Date.now(),
  };
}

export function createWelcomeOfferSync(onMessage) {
  if (typeof window === "undefined") {
    return { publish: () => {}, close: () => {} };
  }

  let channel = null;
  if (typeof window.BroadcastChannel === "function") {
    try {
      channel = new window.BroadcastChannel(WELCOME_OFFER_CHANNEL);
      channel.onmessage = (event) => {
        const message = safeMessage(event?.data);
        if (message) onMessage?.(message);
      };
    } catch (_error) {
      channel = null;
    }
  }

  const onStorage = (event) => {
    if (event.key !== WELCOME_OFFER_SYNC_STORAGE_KEY || !event.newValue) return;
    try {
      const message = safeMessage(JSON.parse(event.newValue));
      if (message) onMessage?.(message);
    } catch (_error) {
      // Ignore malformed values from older builds.
    }
  };
  window.addEventListener("storage", onStorage);

  return {
    publish(value) {
      const message = safeMessage({ ...value, at: Date.now() });
      if (!message) return;
      try {
        channel?.postMessage(message);
      } catch (_error) {
        // Storage remains the compatibility fallback.
      }
      try {
        window.localStorage.setItem(
          WELCOME_OFFER_SYNC_STORAGE_KEY,
          JSON.stringify(message)
        );
        window.localStorage.removeItem(WELCOME_OFFER_SYNC_STORAGE_KEY);
      } catch (_error) {
        // Cross-tab sync is best effort; server idempotence remains authoritative.
      }
      window.dispatchEvent(
        new CustomEvent(WELCOME_OFFER_SYNC_EVENT, { detail: message })
      );
    },
    close() {
      window.removeEventListener("storage", onStorage);
      try {
        channel?.close();
      } catch (_error) {
        // no-op
      }
    },
  };
}

export function dispatchWelcomeOfferRefreshEvents(claim) {
  if (typeof window === "undefined") return;
  const detail = {
    source: "welcome-offer",
    claimId: claim?.claimId || null,
    transactionId: claim?.transactionId || null,
    canonicalTransactionId: claim?.canonicalTransactionId || null,
    wallet_summary: claim?.wallet || null,
    notification: claim?.notification || null,
  };
  window.dispatchEvent(
    new CustomEvent(WALLET_REFRESH_REQUESTED_EVENT, { detail })
  );
  window.dispatchEvent(
    new CustomEvent(REWARDS_REFRESH_REQUESTED_EVENT, { detail })
  );
  // NotificationsPage consumes the canonical record returned by claim/status;
  // this event requests an immediate refresh when that page is already mounted.
  window.dispatchEvent(
    new CustomEvent(NOTIFICATIONS_REFRESH_REQUESTED_EVENT, { detail })
  );
}
